/**
 * Which way the phone is being held, as the photograph needs to know it.
 *
 * Both shells are portrait-only, and a phone with its rotation lock on is
 * portrait-only in the browser too, so the camera's frames are portrait
 * whichever way the phone is turned: a picture taken sideways arrives lying on
 * its side, which is what a phone's own camera app never does. That app reads
 * the accelerometer rather than the interface — its icons turn with the phone
 * even under the rotation lock — and this is the same reading, reduced to the
 * one thing a frame can be told: a quarter-turn.
 *
 * Pure on purpose. Where the reading comes from — a `deviceorientation` event,
 * or `UIDevice` through the iOS plugin — is deviceTilt.ts; this file is the
 * arithmetic, which is the part worth testing without either.
 */

/**
 * Degrees the phone has been turned anticlockwise from upright: 90 is the top
 * edge to the left, 270 to the right. Turning the phone anticlockwise turns
 * the scene in its frame *clockwise* — the frame is fixed to the phone, the
 * scene to the world — so the number is also how far anticlockwise a portrait
 * frame has to be turned back to come out the right way up. This was first
 * written with the two the same way round, and every photograph from a phone
 * on its side came out upside down: the sign here is checked against a
 * photograph, not against the reasoning.
 */
export type Tilt = 0 | 90 | 180 | 270;

/**
 * Past the diagonal by this much before a new quadrant counts. A phone held at
 * exactly 45° would otherwise flip between the two on every reading, and every
 * flip turns the icons — the reading arrives sixty times a second on the web.
 */
const MARGIN = 10;

/**
 * Below this much of gravity across the face of the phone, the phone is lying
 * flat and says nothing about which way is up: the last real reading stands,
 * which is also what a phone's own camera does with a phone pointed straight
 * down at a table.
 */
const FLAT = 0.35;

/**
 * Gravity across the phone's face, in units of g, from a `deviceorientation`
 * reading: `x` toward the right edge, `y` toward the top edge. The spec's
 * rotation order — Z(alpha), X(beta), Y(gamma) — transposed and applied to
 * straight down; alpha drops out, because turning about the vertical changes
 * nothing about which edge is lowest. Upright gives (0, −1); the top edge to
 * the left gives (−1, 0).
 */
export function gravityOf(beta: number, gamma: number): { x: number; y: number } {
  const b = (beta * Math.PI) / 180;
  const g = (gamma * Math.PI) / 180;
  return { x: Math.sin(g) * Math.cos(b), y: -Math.sin(b) };
}

/**
 * The quadrant a gravity reading lands in, with the hysteresis above: the
 * previous answer holds unless the new reading is clearly past a boundary.
 */
export function settleTilt(previous: Tilt, gravity: { x: number; y: number }): Tilt {
  const { x, y } = gravity;
  if (Math.hypot(x, y) < FLAT) return previous;
  // Anticlockwise from upright: upright is (0, −1), the top edge to the left
  // puts gravity along −x.
  let angle = (Math.atan2(-x, -y) * 180) / Math.PI;
  if (angle < 0) angle += 360;
  const candidate = ((Math.round(angle / 90) % 4) * 90) as Tilt;
  if (candidate === previous) return previous;
  const away = Math.abs(((angle - candidate + 540) % 360) - 180);
  return away <= 45 - MARGIN ? candidate : previous;
}

/**
 * The tilt as the *page* sees it. In the shells the page never turns and this
 * is the identity; in a browser that has rotated with the phone the frames
 * have already turned with it (`screen.orientation.angle`, same convention:
 * 90 is anticlockwise), and only what the phone has turned beyond the page is
 * left for the photograph.
 */
export function relativeTilt(tilt: Tilt, screenAngle: number): Tilt {
  return ((((tilt - screenAngle) % 360) + 360) % 360) as Tilt;
}

/**
 * How far to turn the frame clockwise so it comes out upright — the direction
 * snapshot in viewfinder.ts speaks, because a canvas's positive angle is
 * clockwise.
 *
 * The back camera undoes the tilt: the phone went anticlockwise, the scene in
 * its frame went clockwise, and clockwise by 360 − tilt is anticlockwise by
 * the tilt. The front camera is the other way round, and the reason is the
 * mirror: the file is the *unmirrored* frame — a photograph of the person, in
 * which their left is on the right — and a horizontal flip turns every
 * clockwise rotation into an anticlockwise one, flip∘rot(θ) = rot(−θ)∘flip.
 * Gravity that runs toward the phone's left edge runs toward the right edge of
 * that frame, and the turn that brings it down is the tilt itself.
 */
export function rotationFor(tilt: Tilt, facing: 'user' | 'environment'): Tilt {
  return facing === 'user' ? tilt : (((360 - tilt) % 360) as Tilt);
}

/**
 * The nearest angle equivalent to `target` (mod 360) from where `current` is,
 * for a CSS transform that is transitioned: rotate(270deg) from rotate(0deg)
 * animates the long way round, and −90 is the same place by the short one.
 * The result is unbounded on purpose — it is only ever fed back in here.
 */
export function turnTowards(current: number, target: number): number {
  const delta = ((((target - current) % 360) + 540) % 360) - 180;
  return current + delta;
}
